/** ASP.NET JWT role claim URIs */
export const JWT_ROLE_CLAIM_MS =
  'http://schemas.microsoft.com/ws/2008/06/identity/claims/role';
export const JWT_ROLE_CLAIM_SOAP =
  'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/role';

export type PortalUserType = 'Candidate' | 'Provider' | 'Moderator';

/** JWT segments are base64url, which `atob` rejects when the payload contains `-` or `_` or is missing padding. */
export function decodeBase64Url(segment: string): string {
  const base64 = segment.replace(/-/g, '+').replace(/_/g, '/');
  const remainder = base64.length % 4;
  const padded = remainder === 0 ? base64 : base64 + '='.repeat(4 - remainder);
  const binary = atob(padded);
  const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}

export function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const payload = token.split('.')[1];
    if (!payload) {
      return null;
    }
    return JSON.parse(decodeBase64Url(payload)) as Record<string, unknown>;
  } catch {
    return null;
  }
}

export function rolesFromJwtPayload(decoded: Record<string, unknown> | null): string[] {
  if (!decoded) {
    return [];
  }
  const raw =
    decoded['role'] ??
    decoded['Role'] ??
    decoded[JWT_ROLE_CLAIM_MS] ??
    decoded[JWT_ROLE_CLAIM_SOAP];
  if (raw == null) {
    return [];
  }
  if (Array.isArray(raw)) {
    return raw.map((r) => String(r));
  }
  return [String(raw)];
}

export function userTypeFromJwtPayload(
  decoded: Record<string, unknown> | null
): PortalUserType | undefined {
  if (!decoded) {
    return undefined;
  }

  const userType = decoded['user_type'];
  if (typeof userType === 'string' && userType.length > 0) {
    return userType as PortalUserType;
  }

  return undefined;
}

export function permissionsFromJwtPayload(decoded: Record<string, unknown> | null): string[] {
  if (!decoded) {
    return [];
  }

  const raw = decoded['permission'];
  if (raw == null) {
    return [];
  }
  if (Array.isArray(raw)) {
    return raw.map((p) => String(p));
  }
  return [String(raw)];
}

export function staffRoleFromJwtPayload(decoded: Record<string, unknown> | null): string | undefined {
  if (!decoded) {
    return undefined;
  }

  const staffRole = decoded['staff_role'];
  return typeof staffRole === 'string' && staffRole.length > 0 ? staffRole : undefined;
}
